/**
 * Rechtschreibprüfung für häufige deutsche Tippfehler
 * Fokus auf Geburtstag, Jubiläum und Glückwunsch-Texte
 */

const TYPO_MAP = [
  // Geburtstag-Varianten
  { pattern: /Geburtztag/gi, suggestion: 'Geburtstag' },
  { pattern: /Geburtzstag/gi, suggestion: 'Geburtstag' },
  { pattern: /Geburttag/gi, suggestion: 'Geburtstag' },
  { pattern: /Gebursttag/gi, suggestion: 'Geburtstag' },
  { pattern: /Geburstag/gi, suggestion: 'Geburtstag' },
  { pattern: /Geburtsag/gi, suggestion: 'Geburtstag' },

  // Glückwunsch-Varianten
  { pattern: /Glückwuntsch/gi, suggestion: 'Glückwunsch' },
  { pattern: /Glükwunsch/gi, suggestion: 'Glückwunsch' },
  { pattern: /Glückwunch/gi, suggestion: 'Glückwunsch' },
  { pattern: /Glückwnusch/gi, suggestion: 'Glückwunsch' },
  { pattern: /Glückwusch/gi, suggestion: 'Glückwunsch' },
  { pattern: /Glüchwunsch/gi, suggestion: 'Glückwunsch' },

  // Herzlichen
  { pattern: /Herzlischen/gi, suggestion: 'Herzlichen' },
  { pattern: /Herziche?n/gi, suggestion: 'Herzlichen' },
  { pattern: /Herlichen/gi, suggestion: 'Herzlichen' },
  { pattern: /Herzlihen/gi, suggestion: 'Herzlichen' },

  // Jubiläum
  { pattern: /Jubiläeum/gi, suggestion: 'Jubiläum' },
  { pattern: /Jubileum/gi, suggestion: 'Jubiläum' },
  { pattern: /Jubileeum/gi, suggestion: 'Jubiläum' },
  { pattern: /Jubiläm/gi, suggestion: 'Jubiläum' },
  { pattern: /Jubliäum/gi, suggestion: 'Jubiläum' },

  // Alles Gute
  { pattern: /Alles Gutte/gi, suggestion: 'Alles Gute' },
  { pattern: /Ales Gute/gi, suggestion: 'Alles Gute' },

  // Hochzeit
  { pattern: /Hochziet/gi, suggestion: 'Hochzeit' },
  { pattern: /Hohzeit/gi, suggestion: 'Hochzeit' },
  { pattern: /Hochziet/gi, suggestion: 'Hochzeit' },

  // Einladung
  { pattern: /Einladdung/gi, suggestion: 'Einladung' },
  { pattern: /Einladug/gi, suggestion: 'Einladung' },
  { pattern: /Einladnug/gi, suggestion: 'Einladung' },

  // Willkommen
  { pattern: /Wilkommen/gi, suggestion: 'Willkommen' },
  { pattern: /Willkomen/gi, suggestion: 'Willkommen' },

  // Feier / feiern
  { pattern: /Feier(?=n\b)/gi, suggestion: 'Feier' }, // skip, feiern is fine
  { pattern: /Feiier/gi, suggestion: 'Feier' },

  // Wunderbar / Wunderschön
  { pattern: /Wunderschöhn/gi, suggestion: 'Wunderschön' },
  { pattern: /Wunderschoen/gi, suggestion: 'Wunderschön' },

  // Überraschung
  { pattern: /Überaschung/gi, suggestion: 'Überraschung' },
  { pattern: /Überraschug/gi, suggestion: 'Überraschung' },
  { pattern: /Üeberraschung/gi, suggestion: 'Überraschung' },

  // Gratulation
  { pattern: /Gratullation/gi, suggestion: 'Gratulation' },
  { pattern: /Gratulaion/gi, suggestion: 'Gratulation' },

  // Liebe Grüße
  { pattern: /Liebe Grüsse/gi, suggestion: 'Liebe Grüße' },
  { pattern: /Liebe Grüse/gi, suggestion: 'Liebe Grüße' },

  // das/dass — nur offensichtliche Fälle
  { pattern: /\b(weil|obwohl|damit|sodass|falls|wenn|da|ob)\s+das\b/gi, suggestion: 'dass (nach Konjunktion)' },

  // Doppelbuchstaben-Fehler
  { pattern: /Brüke\b/gi, suggestion: 'Brücke' },
  { pattern: /Glük\b/gi, suggestion: 'Glück' },
  { pattern: /Stück\b/gi, suggestion: null }, // correct, skip
  { pattern: /Stük\b/gi, suggestion: 'Stück' },
  { pattern: /Glüklich/gi, suggestion: 'Glücklich' },
  { pattern: /Vergessen/gi, suggestion: null }, // correct
  { pattern: /Vergesen/gi, suggestion: 'Vergessen' },

  // Häufige Wörter bei Schildern/Bannern
  { pattern: /Wilkomen/gi, suggestion: 'Willkommen' },
  { pattern: /Herrzlich/gi, suggestion: 'Herzlich' },
  { pattern: /Schön das/gi, suggestion: 'Schön, dass' },
  { pattern: /Toll das du/gi, suggestion: 'Toll, dass du' },

  // Ruhestand
  { pattern: /Ruhstand/gi, suggestion: 'Ruhestand' },
  { pattern: /Ruhesand/gi, suggestion: 'Ruhestand' },

  // Abschied
  { pattern: /Abschid\b/gi, suggestion: 'Abschied' },
  { pattern: /Abshied/gi, suggestion: 'Abschied' },

  // Schulanfang
  { pattern: /Schulanfag\b/gi, suggestion: 'Schulanfang' },
  { pattern: /Schuhlanfang/gi, suggestion: 'Schulanfang' },
];

/**
 * Prüft einen Text auf häufige Tippfehler.
 * @param {string} text - Der zu prüfende Text
 * @returns {Array<{word: string, suggestion: string, position: number}>}
 */
export function checkText(text) {
  if (!text || text.length < 2) return [];

  const warnings = [];
  const seen = new Set(); // Keine doppelten Warnungen

  for (const entry of TYPO_MAP) {
    if (entry.suggestion === null) continue; // Korrekte Wörter überspringen

    let match;
    // Reset lastIndex for global regex
    entry.pattern.lastIndex = 0;

    while ((match = entry.pattern.exec(text)) !== null) {
      const key = `${match.index}:${match[0]}`;
      if (seen.has(key)) continue;
      seen.add(key);

      warnings.push({
        word: match[0],
        suggestion: entry.suggestion,
        position: match.index,
      });
    }
  }

  // Nach Position sortieren
  warnings.sort((a, b) => a.position - b.position);
  return warnings;
}

/**
 * Ersetzt alle gefundenen Tippfehler im Text automatisch.
 * @param {string} text - Der Originaltext
 * @param {Array<{word: string, suggestion: string, position: number}>} warnings - Die Warnungen
 * @returns {string} - Der korrigierte Text
 */
export function autoFixText(text, warnings) {
  if (!warnings || warnings.length === 0) return text;

  let result = text;
  // Von hinten nach vorne ersetzen, damit die Positionen stimmen
  const sorted = [...warnings].sort((a, b) => b.position - a.position);

  for (const w of sorted) {
    const before = result.substring(0, w.position);
    const after = result.substring(w.position + w.word.length);
    // Groß-/Kleinschreibung der Suggestion anpassen
    let fix = w.suggestion;
    if (w.suggestion.includes('(')) {
      // Hinweis-Suggestions (z.B. "dass (nach Konjunktion)") → nur "dass" nehmen
      fix = w.suggestion.split(' (')[0];
    }
    result = before + fix + after;
  }

  return result;
}
